/**
 * CITY FILING QUEUE WALKTHROUGH — static help page for the office.
 *
 * Written from the 7/16 office walkthrough. Announced via the
 * 'city_filing_walkthrough_jul17' feature_announcements row (migration 399);
 * the Command Center shows a first-open banner linking here until each user
 * acknowledges it. Plain language, numbered steps, print-friendly.
 */
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { BookOpen } from "lucide-react";

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3 leading-snug">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                        {n}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm leading-6">{children}</CardContent>
        </Card>
    );
}

export default function FilingWalkthroughPage() {
    return (
        <div className="mx-auto max-w-3xl space-y-4">
            <PageHeader
                icon={<BookOpen className="h-6 w-6" aria-hidden />}
                title="How to work the City Filing Queue"
                description="Five minutes, start to finish. Portal sites, mail sites, dropped reports, and missing account numbers."
            />

            <Step n={1} title="Open the City Filing Queue">
                <p>
                    The queue shows only work that&apos;s <strong>ready to file</strong> — nothing half-done sneaks in.
                    You get one card per site. The gray chips along the top are filters: click one to narrow the list,
                    click it again to clear it.
                </p>
            </Step>

            <Step n={2} title="Clear the dropped reports at the top">
                <p>
                    Dropped reports sit at the top of the queue and each one needs exactly <strong>one answer</strong>{" "}
                    from you: pick the right device it belongs to, or dismiss it if it&apos;s not actually a report.
                    Dismissing asks you to confirm first, so you can&apos;t fat-finger one away.
                </p>
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                    Known bug: the report for SN 288385 currently errors with &quot;no tested job&quot;. A fix is on the
                    way — leave that one alone for now.
                </p>
            </Step>

            <Step n={3} title="Portal (online) sites — the indigo cards">
                <p>
                    For cities that take filings through a web portal — VEPO and BSI are the two filing websites
                    different cities use:
                </p>
                <ol className="list-decimal space-y-1 pl-5">
                    <li>Log into the portal using the link right on the card.</li>
                    <li>File the report there like normal.</li>
                    <li>Download the stamped PDF the portal gives you.</li>
                    <li>
                        Drop that PDF onto the queue&apos;s drop zone — it attaches itself by serial number and marks
                        the device submitted automatically.
                    </li>
                    <li>
                        If the portal gave you a confirmation number, record it on the device row so it&apos;s there
                        forever.
                    </li>
                </ol>
            </Step>

            <Step n={4} title="Mail / email (paper) sites — the orange cards">
                <p>
                    The tech&apos;s completed report is <strong>already attached</strong> — nothing to hunt down. Three
                    buttons, three different jobs:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                    <li>
                        <strong>Email city</strong> — shows you the recipient and the PDFs before anything sends, then
                        marks the devices submitted.
                    </li>
                    <li>
                        <strong>Print mailing pack</strong> — builds the cover sheet plus the reports for real, physical
                        mail.
                    </li>
                    <li>
                        <strong>Email report to customer</strong> — sends the customer their copy. This one{" "}
                        <em>never</em> marks anything as filed.
                    </li>
                </ul>
            </Step>

            <Step n={5} title="Amber MISSING account numbers (portal sites only)">
                <p>
                    See an amber <strong>MISSING</strong> chip? Click it, type the account number from the portal, press
                    Enter. It saves everywhere at once — you&apos;ll never enter it again.
                </p>
            </Step>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle>Finding old reports</CardTitle>
                </CardHeader>
                <CardContent className="text-sm leading-6">
                    <p>
                        <Link
                            to="/test-reports"
                            className="font-medium text-primary underline-offset-2 hover:underline"
                        >
                            Records → Test Reports
                        </Link>{" "}
                        — search by serial number, site, or customer.
                    </p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle>Money questions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm leading-6">
                    <p>
                        <strong>More → Finance → Money</strong> — it&apos;s all one page now, with tabs across the top.
                    </p>
                    <p>
                        <strong>Look, don&apos;t push:</strong> anything that charges a card or syncs money waits for
                        Corbin.
                    </p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle>Don&apos;t touch (until Corbin&apos;s in)</CardTitle>
                </CardHeader>
                <CardContent className="text-sm leading-6">
                    <ul className="list-disc space-y-1 pl-5">
                        <li>The amber pre-7/13 backlog banner.</li>
                        <li>The push buttons on the QuickBooks tab.</li>
                    </ul>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle>If a button complains</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm leading-6">
                    <p>
                        The error messages are real sentences — read them, they&apos;ll usually tell you exactly
                        what&apos;s wrong. Screenshot anything weird and send it to Corbin.
                    </p>
                    <p>
                        And relax: <strong>nothing hard-deletes</strong>. Dismissals and waives are all reversible.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
